import type { QueryClient, QueryKey } from 'react-query';
import type { AudienceResponse } from 'api/new-analytics/types';

/**
 * Marks a query stale (without an immediate refetch) when its response is
 * empty/incomplete, so the next mount refetches it instead of the long
 * `staleTime` pinning the incomplete data. Complete responses keep the normal
 * stale window (the tab-switch behavior is preserved). `refetchActive: false`
 * avoids a refetch loop while the same empty response keeps coming back.
 */
export function markAnalyticsQueryStaleIfEmpty(
  queryClient: QueryClient,
  key: QueryKey,
  isEmpty: boolean,
): void {
  if (!isEmpty) {
    return;
  }
  queryClient.invalidateQueries(key, {
    refetchActive: false,
    refetchInactive: false,
  });
}

/**
 * Audience is "incomplete" when it reports a total audience but carries no
 * breakdowns at all (the reported symptom: only a total number, empty map).
 * A genuinely empty period (no total, no breakdowns) is not treated as
 * incomplete so we don't refetch periods that legitimately have no data.
 */
export function isAudienceResponseIncomplete(
  data: AudienceResponse | undefined,
): boolean {
  if (!data) {
    return false;
  }
  const hasBreakdown =
    data.geo.length > 0 ||
    data.gender.length > 0 ||
    data.age.length > 0 ||
    data.city.length > 0 ||
    data.per_platform.length > 0;
  return (data.total ?? 0) > 0 && !hasBreakdown;
}
